import * as prismic from '@prismicio/client'
import { enableAutoPreviews } from '@prismicio/next'
import { setFullLocale } from './utils/utils'

let redis
if (
  typeof window === 'undefined' &&
  process.env.REDIS_URL &&
  process.env.PRISMIC_CACHE_KEY
) {
  import('ioredis').then((Redis) => {
    redis = new Redis.default(process.env.REDIS_URL)
  })
}

const endpoint = process.env.PRISMIC_URL

// How often we re-check the repository master ref (in ms)
const REPO_REF_CHECK_INTERVAL_MS = Number(60 * 1000)

// Fetch latest master ref directly from the repository endpoint
const fetchLatestMasterRef = async () => {
  try {
    if (typeof window !== 'undefined') return null

    const axios = (await import('axios')).default
    const res = await axios.get(endpoint)
    const refs = res.data && Array.isArray(res.data.refs) ? res.data.refs : []
    const master = refs.find((r) => r.id === 'master') || refs[0]
    return master ? master.ref : null
  } catch (e) {
    console.error('Error fetching Prismic master ref')
    return null
  }
}

// Get master ref with a lightweight Redis cache to avoid frequent network calls
const getMasterRef = async () => {
  if (typeof window !== 'undefined') return null

  const baseKey = process.env.PRISMIC_CACHE_KEY
    ? `prismic:${process.env.PRISMIC_CACHE_KEY}:repo`
    : null

  const refKey = baseKey ? `${baseKey}:masterRef` : null
  const checkedAtKey = baseKey ? `${baseKey}:masterRefCheckedAt` : null

  // If redis is not available, just fetch directly
  if (!redis || !refKey || !checkedAtKey) {
    return await fetchLatestMasterRef()
  }

  try {
    const now = Date.now()
    const lastCheckedAtStr = await redis.get(checkedAtKey)
    const lastCheckedAt = lastCheckedAtStr ? Number(lastCheckedAtStr) : 0

    if (lastCheckedAt && now - lastCheckedAt < REPO_REF_CHECK_INTERVAL_MS) {
      const cachedRef = await redis.get(refKey)
      if (cachedRef) return cachedRef
    }

    const latestRef = await fetchLatestMasterRef()
    if (latestRef) {
      // Keep a short TTL; values will be refreshed on schedule
      await redis.set(refKey, latestRef, 'EX', 60 * 5)
      await redis.set(checkedAtKey, String(now), 'EX', 60 * 5)
    }
    return latestRef
  } catch (e) {
    // On error, fallback to direct fetch
    return await fetchLatestMasterRef()
  }
}

export const createClient = (config = {}) => {
  const client = prismic.createClient(endpoint, {
    cachedRepository: true,
    cachedRepositoryExpiration: 60 * 15 * 1000, // 15 minutes
  })

  enableAutoPreviews({
    client,
    previewData: config.previewData,
    req: config.req,
  })

  return client
}

export const getSingleDocument = async (client, lang, document) => {
  // Ensure we use the latest repository ref on server to bypass stale cached repo
  const masterRef = await getMasterRef()

  // Include masterRef in the cache key so that a new ref automatically creates a new cache entry
  const cacheKey = process.env.PRISMIC_CACHE_KEY
    ? `prismic:${process.env.PRISMIC_CACHE_KEY}:${document}:${lang}:${
        masterRef || 'no-ref'
      }`
    : null

  try {
    if (typeof window !== 'undefined') {
      return await client.getSingle(document, { lang })
    }

    if (!redis || !cacheKey) {
      const options = masterRef ? { lang, ref: masterRef } : { lang }
      return await client.getSingle(document, options)
    }

    const cachedData = await redis.get(cacheKey)
    if (cachedData) {
      return JSON.parse(cachedData)
    }

    const options = masterRef ? { lang, ref: masterRef } : { lang }
    const data = await client.getSingle(document, options)
    if (data) {
      await redis.set(cacheKey, JSON.stringify(data), 'EX', 60 * 15)
    }
    return data
  } catch (error) {
    console.error(`Error fetching document "${document}" in "${lang}":`)
    // Éviter de retenter un appel coûteux si une erreur est survenue
    if (redis && cacheKey)
      await redis.set(cacheKey, JSON.stringify(null), 'EX', 60 * 15)
    return null
  }
}

export const getPrismicDocuments = async (documents, locale, previewData) => {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  const data = await Promise.all(
    Object.values(documents).map((document) =>
      getSingleDocument(client, lang, document)
    )
  )

  return Object.fromEntries(
    Object.keys(documents).map((key, index) => [key, data[index]])
  )
}
